import { A, useParams } from '@solidjs/router';
import { createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { BoxEvents } from '../components/BoxEvents';
import { BoxPassword } from '../components/BoxPassword';
import { BoxStatusBadge, Ec2StatusBadge, SystemStatusBadge } from '../components/BoxStatus';
import { CopyLine } from '../components/CopyLine';
import { DeployStackDialog } from '../components/DeployStackDialog';
import { DestroyBoxDialog } from '../components/DestroyBoxDialog';
import { EmptyState, ErrorAlert, LoadingRows } from '../components/states';
import { archLabel, canDeploy, sshCommand } from '../lib/format';
import { flatten, useBox, useBoxStacks, useSshKeys } from '../lib/queries';
import { Badge, Tag } from '../ui/Badge';
import { Button, buttonClass } from '../ui/Button';
import { Card } from '../ui/Card';
import { Icon } from '../ui/Icon';

export function BoxDetail(): JSX.Element {
  const params = useParams<{ boxId: string }>();
  const box = useBox(() => params.boxId);
  const busy = () => box.data?.box_status === 'Updating';
  // Updating covers both the initial provisioning and a job running on a box
  // that already exists; only the latter has an instance to talk about.
  const provisioning = () => busy() && box.data?.instance_id === null;
  const working = () => busy() && box.data?.instance_id !== null;
  const deployable = () => !!box.data && canDeploy(box.data.box_status, box.data.ec2_status?.system_status);
  const stacks = useBoxStacks(
    () => params.boxId,
    () => box.data !== undefined && !provisioning(),
    busy,
  );
  const deployed = createMemo(() => flatten(stacks.data?.pages));
  const address = () => box.data?.hostname ?? box.data?.ipv4 ?? box.data?.ipv6;

  // ssh_keys on the box record is a list of hashes; the display names live in
  // the user's own key list, so the two are joined here for the box page.
  const sshKeys = useSshKeys();
  const sshKeyItems = createMemo(() => flatten(sshKeys.data?.pages));
  const boxSshKeys = createMemo(() => {
    const byHash = new Map(sshKeyItems().map((key) => [key.hash, key]));
    return (box.data?.ssh_keys ?? []).map((hash) => byHash.get(hash) ?? { hash, display: undefined });
  });

  const [deployOpen, setDeployOpen] = createSignal(false);
  const [destroyOpen, setDestroyOpen] = createSignal(false);

  return (
    <main class="page">
      <A href="/boxes" class="backlink">
        <Icon name="arrowLeft" size={14} />
        Boxes
      </A>

      <Show when={box.isError}>
        <ErrorAlert title="Could not load the box" error={box.error} onRetry={() => void box.refetch()} />
      </Show>

      <Show when={!box.isPending} fallback={<LoadingRows rows={2} height={180} />}>
        <Show
          when={box.data}
          fallback={
            <EmptyState
              icon="box"
              title="Box not found"
              text="It may have been destroyed, or it belongs to someone else."
            />
          }
        >
          {(current) => (
            <>
              <div class="page__head">
                <div class="page__title">
                  <h1 style={{ 'font-size': 'var(--text-2xl)' }}>
                    {current().display}
                  </h1>
                  <div class="row gap-2 wrap">
                    <BoxStatusBadge status={current().box_status} />
                    <Ec2StatusBadge status={current().ec2_status?.instance_state} />
                    <SystemStatusBadge status={current().ec2_status?.system_status} />
                    <span class="page__subtitle">
                      {current().os} {current().version}
                    </span>
                  </div>
                </div>
                <div class="page__actions">
                  <Button variant="secondary" onClick={() => void box.refetch()} loading={box.isFetching}>
                    <Icon name="refresh" />
                    Refresh
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={!deployable()}
                    onClick={() => setDeployOpen(true)}
                  >
                    <Icon name="layers" />
                    Deploy stack
                  </Button>
                  <Button
                    variant="ghost"
                    class="is-danger"
                    disabled={current().box_status === 'Updating'}
                    onClick={() => setDestroyOpen(true)}
                  >
                    <Icon name="trash" />
                    Destroy
                  </Button>
                </div>
              </div>

              <Show when={provisioning()}>
                <div class="alert alert--info" style={{ 'margin-bottom': 'var(--space-5)' }}>
                  <span class="tk-spinner" style={{ 'margin-top': '2px' }} />
                  <div class="alert__body">
                    <span class="alert__title">Setting up</span>
                    <span>Your box is being prepared. This usually takes a few minutes.</span>
                  </div>
                </div>
              </Show>

              <Show when={working()}>
                <div class="alert alert--info" style={{ 'margin-bottom': 'var(--space-5)' }}>
                  <span class="tk-spinner" style={{ 'margin-top': '2px' }} />
                  <div class="alert__body">
                    <span class="alert__title">Working</span>
                    <span>
                      A job is running on this box. Please wait until it finishes.
                    </span>
                  </div>
                </div>
              </Show>

              <Show when={current().box_status === 'Failed'}>
                <div class="alert alert--danger" style={{ 'margin-bottom': 'var(--space-5)' }}>
                  <Icon name="alert" size={18} />
                  <div class="alert__body">
                    <span class="alert__title">Setup failed</span>
                    <span>This box did not come up. Destroy it and create a new one.</span>
                  </div>
                </div>
              </Show>

              <div class="detail-grid">
                <div class="stack gap-5">
                  <div class="connection-grid">
                    <Card padding="lg">
                      <div class="section__title">
                        <h3>Open</h3>
                        <Icon name="globe" size={18} />
                      </div>
                      <Show
                        when={current().hostname}
                        fallback={<span class="muted text-sm">No hostname assigned yet.</span>}
                      >
                        {(hostname) => (
                          <div class="stack gap-3">
                            <a
                              href={`https://${hostname()}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              class={buttonClass('primary')}
                            >
                              <Icon name="globe" />
                              Open in new tab
                            </a>
                            {/* The secret is recorded together with the instance id (box_jobs deploy_box.rs). */}
                            <Show when={current().instance_id !== null}>
                              <BoxPassword boxId={params.boxId} />
                            </Show>
                          </div>
                        )}
                      </Show>
                    </Card>

                    <Card padding="lg">
                      <div class="section__title">
                        <h3>SSH</h3>
                        <Icon name="terminal" size={18} />
                      </div>
                      <Show
                        when={address()}
                        fallback={<span class="muted text-sm">No address assigned yet.</span>}
                      >
                        {(ip) => (
                          <div class="stack gap-3">
                            <CopyLine text={sshCommand(ip())} label="Copy SSH command" />
                            <span class="text-xs muted">
                              Sign in as <code>ubuntu</code> with one of the keys chosen when this box was created.
                            </span>
                          </div>
                        )}
                      </Show>
                    </Card>
                  </div>

                  <Card padding="lg">
                    <div class="section__title">
                      <h3>Deployed stacks</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!deployable()}
                        onClick={() => setDeployOpen(true)}
                      >
                        <Icon name="plus" />
                        Deploy
                      </Button>
                    </div>
                    <Show
                      when={!provisioning()}
                      fallback={<span class="muted text-sm">Available once the box is ready.</span>}
                    >
                      <Show when={!stacks.isPending} fallback={<LoadingRows rows={2} height={64} />}>
                        <Show
                          when={deployed().length > 0}
                          fallback={
                            <span class="muted text-sm">Nothing deployed yet. Pick a stack from the catalog.</span>
                          }
                        >
                          <div class="list">
                            <For each={deployed()}>
                              {(stack) => (
                                <div class="list-row">
                                  <Icon name="layers" size={18} />
                                  <div class="list-row__main">
                                    <span class="list-row__title">{stack.display}</span>
                                    <span class="list-row__meta mono">{stack.stack_dir}</span>
                                  </div>
                                  <Badge tone="brand">rev {stack.revision}</Badge>
                                </div>
                              )}
                            </For>
                          </div>
                        </Show>
                      </Show>
                    </Show>
                    <Show when={stacks.isError}>
                      <ErrorAlert title="Could not load deployed stacks" error={stacks.error} />
                    </Show>
                  </Card>

                  <BoxEvents boxId={params.boxId} busy={busy()} />
                </div>

                <div class="stack gap-5">
                  <Card padding="lg">
                    <div class="section__title">
                      <h3>Instance</h3>
                      <Icon name="cpu" size={18} />
                    </div>
                    <div class="kv">
                      <span class="kv__k">Status</span>
                      <span class="kv__v">{current().box_status}</span>
                      <span class="kv__k">Machine</span>
                      <span class="kv__v">{current().ec2_status?.instance_state ?? '—'}</span>
                      <span class="kv__k">Instance check</span>
                      <span class="kv__v">{current().ec2_status?.instance_status ?? '—'}</span>
                      <span class="kv__k">System check</span>
                      <span class="kv__v">{current().ec2_status?.system_status ?? '—'}</span>
                      <span class="kv__k">Application check</span>
                      <span class="kv__v">{current().ec2_status?.application_status ?? '—'}</span>
                      <span class="kv__k">Instance ID</span>
                      <span class="kv__v kv__v--mono">{current().instance_id ?? '—'}</span>
                      <span class="kv__k">Instance type</span>
                      <span class="kv__v kv__v--mono">{current().instance_type}</span>
                      <span class="kv__k">Architecture</span>
                      <span class="kv__v">{archLabel(current().arch)}</span>
                      <span class="kv__k">Hostname</span>
                      <span class="kv__v kv__v--mono">{current().hostname ?? '—'}</span>
                      <span class="kv__k">IPv4</span>
                      <span class="kv__v kv__v--mono">{current().ipv4 ?? '—'}</span>
                      <span class="kv__k">IPv6</span>
                      <span class="kv__v kv__v--mono">{current().ipv6 ?? '—'}</span>
                      <span class="kv__k">OS</span>
                      <span class="kv__v">
                        {current().os} {current().version}
                      </span>
                      <span class="kv__k">Box ID</span>
                      <span class="kv__v kv__v--mono">{params.boxId}</span>
                    </div>
                  </Card>

                  <Card padding="lg">
                    <div class="section__title">
                      <h3>SSH keys</h3>
                      <Icon name="key" size={18} />
                    </div>
                    <Show
                      when={boxSshKeys().length > 0}
                      fallback={<span class="muted text-sm">No keys installed on this box.</span>}
                    >
                      <span class="row gap-2 wrap">
                        <For each={boxSshKeys()}>
                          {(key) => <Tag>{key.display ?? key.hash}</Tag>}
                        </For>
                      </span>
                    </Show>
                  </Card>

                  <Card padding="lg">
                    <div class="section__title">
                      <h3>LiteLLM key</h3>
                      <Icon name="key" size={18} />
                    </div>
                    <Show
                      when={current().litellm_key}
                      fallback={<span class="muted text-sm">Not provisioned yet.</span>}
                    >
                      {(key) => (
                        <span class="row gap-2 wrap">
                          <Tag>{key()}</Tag>
                        </span>
                      )}
                    </Show>
                  </Card>
                </div>
              </div>

              <DeployStackDialog
                open={deployOpen()}
                box={{ box_id: params.boxId, display: current().display }}
                onClose={() => setDeployOpen(false)}
              />
              <DestroyBoxDialog
                box={
                  destroyOpen()
                    ? {
                        box_id: params.boxId,
                        display: current().display,
                        box_status: current().box_status,
                        instance_id: current().instance_id,
                        hostname: current().hostname,
                      }
                    : undefined
                }
                onClose={() => setDestroyOpen(false)}
              />
            </>
          )}
        </Show>
      </Show>
    </main>
  );
}
